import { useLocalNumber } from './useLocalNumber';
import { FieldTooltip } from './FieldTooltip';

interface Vec3InputProps {
  label: string;
  tooltip?: string;
  value: [number, number, number];
  onChange: (value: [number, number, number]) => void;
  /** Called when the user finishes editing any axis (on blur). Use to commit an undo entry. */
  onCommit?: () => void;
  disabled?: boolean;
  onAdjust?: () => void;
  isAdjusting?: boolean;
}

export const Vec3Input = ({ label, tooltip, value, onChange, onCommit, disabled, onAdjust, isAdjusting }: Vec3InputProps) => {
  const x = useLocalNumber(value[0], (n) => onChange([n, value[1], value[2]]));
  const y = useLocalNumber(value[1], (n) => onChange([value[0], n, value[2]]));
  const z = useLocalNumber(value[2], (n) => onChange([value[0], value[1], n]));

  return (
    <div className="flex flex-col gap-1">
      <span className="flex items-center gap-1.5 text-xs text-[#999]">
        {label}
        {tooltip && <FieldTooltip text={tooltip} />}
        {onAdjust && (
          <button
            type="button"
            onClick={onAdjust}
            className={`text-xs px-1.5 py-0.5 rounded border cursor-pointer transition-colors ${
              isAdjusting
                ? 'text-[#C3D82E] bg-[#2A2A2A] border-[#C3D82E]'
                : 'text-[#888] border-[#444] hover:text-[#CCC] hover:border-[#666] hover:bg-[#2A2A2A]'
            }`}
          >
            Adjust on model
          </button>
        )}
      </span>
      <div className="flex gap-2">
        {([
          { axis: 'X', ...x },
          { axis: 'Y', ...y },
          { axis: 'Z', ...z },
        ] as const).map(({ axis, local, handleChange, handleBlur }) => (
          <label key={axis} className="flex flex-col items-center gap-0.5 flex-1">
            <span className="text-xs text-[#777]">{axis}</span>
            <input
              type="text"
              inputMode="decimal"
              value={local}
              disabled={disabled}
              onChange={(e) => handleChange(e.target.value)}
              onBlur={() => { handleBlur(); onCommit?.(); }}
              className="w-full rounded border border-[#444] bg-[#2A2A2A] px-2 py-1 text-xs text-[#E8E8E8] focus:outline-none focus:border-[#C3D82E] disabled:opacity-50"
            />
          </label>
        ))}
      </div>
    </div>
  );
};
